import { parseBackendDateToLocal, toLocaleByLanguage } from './dateTime';
import { dayKey, daysBetween, getActiveTimeZone, keyToUtcDate } from './zonedTime';

type DateInput = string | number | Date | null | undefined;

const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return parseBackendDateToLocal(value);
};

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Instants show in the account's time zone; plain days (due dates) never shift. */
export const formatDate = (
  value: DateInput,
  language?: string,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
): string => {
  if (typeof value === 'string' && DATE_ONLY.test(value.trim())) {
    return new Intl.DateTimeFormat(toLocaleByLanguage(language), { ...options, timeZone: 'UTC' })
      .format(keyToUtcDate(value.trim()));
  }
  const date = toDate(value);
  if (!date) return typeof value === 'string' ? value : '';
  return new Intl.DateTimeFormat(toLocaleByLanguage(language), { timeZone: getActiveTimeZone(), ...options })
    .format(date);
};

export const formatDateTime = (
  value: DateInput,
  language?: string,
  options?: Intl.DateTimeFormatOptions
): string => formatDate(value, language, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  ...options,
});

export const formatTime = (value: DateInput, language?: string): string =>
  formatDate(value, language, { hour: '2-digit', minute: '2-digit' });

/** `value` is on a 0–100 scale; output follows the locale ("%50" in Turkish, "50%" in English). */
export const formatPercent = (value: number, language?: string): string => {
  const safe = Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : 0;
  return new Intl.NumberFormat(toLocaleByLanguage(language), {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(Math.round(safe) / 100);
};

/** Calendar-day based: today → time, 1–6 days → "yesterday" / "3 days ago", older → date. */
export const formatRelativeDay = (value: DateInput, language?: string, now: Date = new Date()): string => {
  const date = toDate(value);
  if (!date) return '';
  const diffDays = daysBetween(dayKey(date), dayKey(now));
  if (diffDays === 0) return formatTime(date, language);
  if (diffDays > 0 && diffDays < 7) {
    return new Intl.RelativeTimeFormat(toLocaleByLanguage(language), { numeric: 'auto' }).format(-diffDays, 'day');
  }
  return formatDate(date, language);
};

/** "1 h 30 min" / "1 sa 30 dk" / "45 sec": short units in the language's own form. */
export const formatDurationShort = (seconds: number, language?: string): string => {
  if (!seconds || seconds <= 0) return '';
  const locale = toLocaleByLanguage(language);
  const unit = (value: number, name: 'hour' | 'minute' | 'second') =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: name, unitDisplay: 'short' }).format(value);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  // A whole hour reads "2 hr", not "2 hr 0 min"
  if (h > 0) return m > 0 ? `${unit(h, 'hour')} ${unit(m, 'minute')}` : unit(h, 'hour');
  if (m > 0) return unit(m, 'minute');
  return unit(seconds, 'second');
};
